import { isDefined } from '@aloft/toolkit-shared';
import { Button, Flex, Typography, getSortIcon } from '@aloft/toolkit-sui';
// eslint-disable-next-line aloft/no-sui-chakra
import { ChakraProps, chakra } from '@aloft/toolkit-sui/chakra';
import {
  DndContext,
  DragEndEvent,
  DragOverEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { SortableContext, arrayMove, horizontalListSortingStrategy } from '@dnd-kit/sortable';
import { Header, Row as ReactTableRow, Table, flexRender } from '@tanstack/react-table';

import {
  Fragment,
  ReactNode,
  useCallback,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

import { DraggableHeader } from './components/DraggableHeader.tsx';
import { MemoizedRow } from './components/MemoizedRow.tsx';
import { Row } from './components/Row.tsx';
import {
  HEADER_HEIGHT_IN_PX,
  PADDING_X_IN_PX,
  PINNED_SCROLL_SHADOW,
  PinnedColumnEdge,
  sharedCellStyles,
} from './constants.ts';
import { getCellWidthStyles } from './getCellWidthStyles.ts';
import { TPTableClickRow } from './types.ts';
import { usePinnedColumnStyles } from './usePinnedColumnStyles.ts';
import { useVirtualizedRows } from './useVirtualizedRows.ts';
import { makeRowID } from './utils/makeRowID.ts';

const GROUP_ROW_ENTER_MS = 200;

export type TableRowGrouping<T> = {
  getGroupId: (row: T) => string | undefined;
  groups: {
    id: string;
    count: number;
    collapsed: boolean;
    renderHeader: (colSpan: number, stickyTop: string) => ReactNode;
  }[];
};

type Props<T> = {
  table: Table<T>;
  shouldPinFirstRow: boolean | number;
  activeRowIndex?: number | null;
  hoveredRowId?: string;
  emptyTableMessage?: string;
  onRowClick?: TPTableClickRow<T>;
  onRowHoverChange?: (row: T | undefined) => void;
  __dangerOverrideInternalSelectionState?: boolean;
  memoizeRows?: boolean;
  getRowBackgroundColor?: (row: ReactTableRow<T>) => string | undefined;
  useVirtualization?: boolean;
  rowGrouping?: TableRowGrouping<T>;
  enableColumnReorder?: boolean;
  onColumnOrderChange?: (columnOrder: string[]) => void;
  pinnedColumnEdge?: PinnedColumnEdge;
};

export const ToolkitPropertyTableUI = <T,>({
  table,
  shouldPinFirstRow,
  activeRowIndex = null,
  hoveredRowId,
  emptyTableMessage = 'No properties found',
  onRowClick,
  onRowHoverChange,
  __dangerOverrideInternalSelectionState = false,
  memoizeRows = false,
  getRowBackgroundColor,
  useVirtualization = true,
  rowGrouping,
  enableColumnReorder = false,
  onColumnOrderChange,
  pinnedColumnEdge = 'border',
}: Props<T>) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const virtualizationEnabled = useVirtualization && !rowGrouping;
  const [isHorizontallyScrolled, setIsHorizontallyScrolled] = useState(false);
  const [activeHeaderId, setActiveHeaderId] = useState<string | null>(null);
  const [dropIndicator, setDropIndicator] = useState<{
    id: string;
    side: 'left' | 'right';
  } | null>(null);
  const [groupHeaderStickyTop, setGroupHeaderStickyTop] = useState(`${HEADER_HEIGHT_IN_PX}px`);

  const numPinnedColumns = table.getState().columnPinning.left?.length ?? 0;
  const pinnedColumnStyles = usePinnedColumnStyles({
    paddingX: PADDING_X_IN_PX,
    numColumns: numPinnedColumns,
    edge: pinnedColumnEdge,
  });

  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 8 } }));

  useEffect(() => {
    const el = containerRef.current;
    if (!el || pinnedColumnEdge !== 'scrollShadow') return;

    const onScroll = () => setIsHorizontallyScrolled(el.scrollLeft > 0);
    onScroll();
    el.addEventListener('scroll', onScroll, { passive: true });
    return () => el.removeEventListener('scroll', onScroll);
  }, [pinnedColumnEdge]);

  const { rows: reactTableRows } = table.getRowModel();
  const pinnedCount = Number(shouldPinFirstRow);
  const groupCollapsedKey = rowGrouping?.groups.map((g) => `${g.id}:${g.collapsed}`).join('|');
  const prevGroupCollapsedRef = useRef<Record<string, boolean>>({});
  const [enteringGroupIds, setEnteringGroupIds] = useState<ReadonlySet<string>>(() => new Set());

  useLayoutEffect(() => {
    if (!rowGrouping) return;
    const tbody = containerRef.current?.querySelector('tbody');
    if (!tbody) return;

    const measure = () => {
      let height = 0;
      const rows = tbody.children;
      for (let i = 0; i < pinnedCount && i < rows.length; i++) {
        height += (rows[i] as HTMLElement).getBoundingClientRect().height;
      }
      setGroupHeaderStickyTop(`${HEADER_HEIGHT_IN_PX + height}px`);
    };

    measure();
    const observer = new ResizeObserver(measure);
    observer.observe(tbody);
    return () => observer.disconnect();
  }, [pinnedCount, rowGrouping]);

  useLayoutEffect(() => {
    if (!rowGrouping) {
      prevGroupCollapsedRef.current = {};
      return;
    }

    const newlyEntering = new Set<string>();
    rowGrouping.groups.forEach((group) => {
      const wasCollapsed = prevGroupCollapsedRef.current[group.id];
      if (wasCollapsed === true && !group.collapsed) {
        newlyEntering.add(group.id);
      }
      prevGroupCollapsedRef.current[group.id] = group.collapsed;
    });

    if (newlyEntering.size === 0) return;

    setEnteringGroupIds(newlyEntering);
    const timeoutId = window.setTimeout(() => {
      setEnteringGroupIds(new Set());
    }, GROUP_ROW_ENTER_MS);
    return () => window.clearTimeout(timeoutId);
  }, [groupCollapsedKey, rowGrouping]);

  const { virtualRows, virtualPaddingTop, virtualPaddingBottom } = useVirtualizedRows({
    numRows: reactTableRows.length,
    shouldPinFirstRow,
    containerRef,
    overscan: 7,
    enabled: virtualizationEnabled,
  });

  const handleRowClick = useCallback(
    (row: ReactTableRow<T>) => {
      onRowClick?.(row.original);
    },
    [onRowClick],
  );

  const handleRowHover = useCallback(
    (row: ReactTableRow<T>, isHovered: boolean) => {
      onRowHoverChange?.(isHovered ? row.original : undefined);
    },
    [onRowHoverChange],
  );

  const reorderableColumnIds = enableColumnReorder
    ? table.getVisibleLeafColumns().flatMap((column) => (column.getIsPinned() ? [] : [column.id]))
    : [];

  const handleDragStart = useCallback((event: DragStartEvent) => {
    setActiveHeaderId(String(event.active.id));
  }, []);

  const handleDragOver = useCallback(
    (event: DragOverEvent) => {
      const { active, over } = event;
      if (!over || active.id === over.id) {
        setDropIndicator((prev) => (prev ? null : prev));
        return;
      }

      const currentOrder = table.getAllLeafColumns().map((column) => column.id);
      const activeIndex = currentOrder.indexOf(String(active.id));
      const overIndex = currentOrder.indexOf(String(over.id));
      if (activeIndex === -1 || overIndex === -1) return;

      const next = {
        id: String(over.id),
        side: (activeIndex < overIndex ? 'right' : 'left') as 'left' | 'right',
      };
      setDropIndicator((prev) => (prev?.id === next.id && prev.side === next.side ? prev : next));
    },
    [table],
  );

  const handleDragEnd = useCallback(
    (event: DragEndEvent) => {
      setActiveHeaderId(null);
      setDropIndicator(null);
      const { active, over } = event;
      if (!over || active.id === over.id) return;

      const currentOrder = table.getAllLeafColumns().map((column) => column.id);
      const oldIndex = currentOrder.indexOf(String(active.id));
      const newIndex = currentOrder.indexOf(String(over.id));
      if (oldIndex === -1 || newIndex === -1) return;

      const pinnedIds = table.getState().columnPinning.left ?? [];
      if (pinnedIds.includes(String(active.id)) || pinnedIds.includes(String(over.id))) return;

      onColumnOrderChange?.(arrayMove(currentOrder, oldIndex, newIndex));
    },
    [onColumnOrderChange, table],
  );

  const rows = memoizeRows || !virtualizationEnabled ? reactTableRows : virtualRows;
  const pinnedRows = virtualizationEnabled ? reactTableRows.slice(0, pinnedCount) : [];
  const totalPages = table.getPageCount();
  const currentPage = table.getState().pagination.pageIndex + 1;
  const colSpan = table.getVisibleLeafColumns().length;

  const groupedBody = useMemo(() => {
    if (!rowGrouping) return null;

    const pinned = reactTableRows.slice(0, pinnedCount);
    const unpinned = reactTableRows.slice(pinnedCount);
    const rowsByGroup = new Map<string, ReactTableRow<T>[]>();
    const ungrouped: ReactTableRow<T>[] = [];

    rowGrouping.groups.forEach((group) => rowsByGroup.set(group.id, []));
    unpinned.forEach((row) => {
      const groupId = rowGrouping.getGroupId(row.original);
      if (groupId && rowsByGroup.has(groupId)) {
        rowsByGroup.get(groupId)!.push(row);
      } else {
        ungrouped.push(row);
      }
    });

    return { pinned, rowsByGroup, ungrouped };
  }, [pinnedCount, reactTableRows, rowGrouping]);

  const isTableEmpty = rows.length === 0;
  const RowComponent = memoizeRows ? MemoizedRow : Row;

  const renderDataRow = (row: ReactTableRow<T>, animateEnter = false) => (
    <RowComponent
      key={row.id}
      id={__dangerOverrideInternalSelectionState ? undefined : makeRowID(row.index)}
      isSelected={
        __dangerOverrideInternalSelectionState
          ? null
          : !!table.getSelectedRowModel().rowsById[row.id]
      }
      isHovered={row.id === hoveredRowId}
      row={row}
      activeRowIndex={activeRowIndex}
      shouldPinFirstRow={shouldPinFirstRow}
      handleRowClick={handleRowClick}
      handleRowHover={handleRowHover}
      enableHoverStyle={isDefined(onRowClick)}
      pinnedColumnStyles={pinnedColumnStyles}
      customBackgroundColor={getRowBackgroundColor?.(row)}
      animateEnter={animateEnter}
    />
  );

  const renderHeader = (header: Header<T, unknown>) => {
    const canSort = header.column.getCanSort();
    const canDrag = enableColumnReorder && !header.column.getIsPinned();
    pinnedColumnStyles.setStyleInfo(header.column);

    const styles: ChakraProps = {
      ...sharedCellStyles,
      ...getCellWidthStyles(header.getSize()),
      ...pinnedColumnStyles.getStyles(header.column),
      backgroundColor: 'surface2',
      borderBottom: '2px solid',
      borderColor: 'strokeDefault',
      verticalAlign: 'middle',
    };
    const content = (
      <Flex
        onClick={header.column.getToggleSortingHandler()}
        cursor={canDrag ? undefined : canSort ? 'pointer' : undefined}
        alignItems="center"
        gap={1}
      >
        {flexRender(header.column.columnDef.header, header.getContext())}
        {canSort && getSortIcon(header.column)}
      </Flex>
    );

    if (!enableColumnReorder || !canDrag) {
      return (
        <chakra.th
          key={header.id}
          {...styles}
          _hover={enableColumnReorder ? { backgroundColor: 'surface4' } : undefined}
        >
          {content}
        </chakra.th>
      );
    }

    return (
      <DraggableHeader
        key={header.id}
        id={header.id}
        styles={styles}
        dropSide={dropIndicator?.id === header.id ? dropIndicator.side : undefined}
      >
        {content}
      </DraggableHeader>
    );
  };

  const activeHeader = activeHeaderId
    ? table.getHeaderGroups()[0]?.headers.find((header) => header.id === activeHeaderId)
    : undefined;

  const tableBody = (
    <chakra.table width="100%" style={{ borderCollapse: 'separate', borderSpacing: 0 }}>
      <chakra.thead position="sticky" top="0px" zIndex="3" height={`${HEADER_HEIGHT_IN_PX}px`}>
        {table.getHeaderGroups().map((headerGroup) => (
          <chakra.tr key={headerGroup.id}>
            {headerGroup.headers.map((header) => renderHeader(header))}
          </chakra.tr>
        ))}
      </chakra.thead>
      <chakra.tbody>
        {groupedBody ? (
          <>
            {groupedBody.pinned.map((row) => renderDataRow(row))}
            {rowGrouping!.groups.map((group) => (
              <Fragment key={group.id}>
                {group.renderHeader(colSpan, groupHeaderStickyTop)}
                {!group.collapsed &&
                  (groupedBody.rowsByGroup.get(group.id) ?? []).map((row) =>
                    renderDataRow(row, enteringGroupIds.has(group.id)),
                  )}
              </Fragment>
            ))}
            {groupedBody.ungrouped.map((row) => renderDataRow(row))}
          </>
        ) : (
          <>
            {pinnedRows.map((row) => renderDataRow(row))}
            {!memoizeRows && virtualPaddingTop && (
              <tr>
                <chakra.td height={virtualPaddingTop} />
              </tr>
            )}
            {rows.map((x) => {
              const row =
                memoizeRows || !virtualizationEnabled
                  ? (x as ReactTableRow<T>)
                  : reactTableRows[x.index];
              if (!row) return null;
              return renderDataRow(row);
            })}
            {!memoizeRows && virtualPaddingBottom && (
              <tr>
                <chakra.td height={virtualPaddingBottom} />
              </tr>
            )}
          </>
        )}
      </chakra.tbody>
    </chakra.table>
  );

  return (
    <Flex flexDirection="column" height="100%">
      <chakra.div
        ref={containerRef}
        overflow="auto"
        flex={1}
        minHeight={0}
        minWidth={0}
        position="relative"
        data-scrolled={isHorizontallyScrolled ? 'true' : 'false'}
        sx={
          pinnedColumnEdge === 'scrollShadow' && numPinnedColumns > 0
            ? {
                [`&[data-scrolled="true"] th:nth-of-type(${numPinnedColumns}), &[data-scrolled="true"] td:nth-of-type(${numPinnedColumns})`]:
                  {
                    boxShadow: PINNED_SCROLL_SHADOW,
                    clipPath: 'inset(-8px -12px -8px 0)',
                  },
              }
            : undefined
        }
      >
        {enableColumnReorder ? (
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragStart={handleDragStart}
            onDragOver={handleDragOver}
            onDragEnd={handleDragEnd}
            onDragCancel={() => {
              setActiveHeaderId(null);
              setDropIndicator(null);
            }}
          >
            <SortableContext items={reorderableColumnIds} strategy={horizontalListSortingStrategy}>
              {tableBody}
            </SortableContext>
            <DragOverlay dropAnimation={null}>
              {activeHeader ? (
                <chakra.div
                  opacity={0.75}
                  backgroundColor="surface4"
                  display="flex"
                  alignItems="center"
                  gap="4px"
                  height={`${HEADER_HEIGHT_IN_PX}px`}
                  paddingX={`${PADDING_X_IN_PX}px`}
                  borderBottom="2px solid"
                  borderColor="strokeDefault"
                  cursor="grabbing"
                  whiteSpace="nowrap"
                >
                  {flexRender(activeHeader.column.columnDef.header, activeHeader.getContext())}
                  {activeHeader.column.getCanSort() && getSortIcon(activeHeader.column)}
                </chakra.div>
              ) : null}
            </DragOverlay>
          </DndContext>
        ) : (
          tableBody
        )}
        {isTableEmpty && (
          <Flex padding={4} justifyContent="center">
            <Typography>{emptyTableMessage}</Typography>
          </Flex>
        )}
      </chakra.div>

      {totalPages > 1 && (
        <Flex justifyContent="center" alignItems="center" gap={4} padding={4}>
          <Button
            variant="inlineSecondary"
            onClick={() => table.previousPage()}
            disabled={!table.getCanPreviousPage()}
            size="md"
            text="Previous"
          />
          <Typography>
            Page {currentPage} of {totalPages}
          </Typography>

          <Button
            variant="inlineSecondary"
            onClick={() => table.nextPage()}
            disabled={!table.getCanNextPage()}
            size="md"
            text="Next"
          />
        </Flex>
      )}
    </Flex>
  );
};
